import { Flex, Box, Button, Icon, Circle } from '@chakra-ui/react';
import {
  useFormik,
  FormikProvider,
  FieldArray,
  Form,
  ArrayHelpers,
} from 'formik';
import * as Yup from 'yup';
import { useMemo, useState, useRef } from 'react';
import { FilterRow } from '../FilterRow';
import {
  FILTER_COMPARISON_CONDITIONS,
  FILTER_CONDITIONS,
  FILTER_KINDS,
} from '../../utils/constants';
import { KText } from '@/ui-library/components';
import { Plus } from '@kula-ai/icons-core';

const validationSchema = Yup.object().shape({
  filters: Yup.array().of(
    Yup.object().shape({
      filter_by: Yup.object().required('Filter term is required'),
      filter_condition: Yup.object().required('Filter condition is required'),
      filter_by_values: Yup.array().required('Filter value is required'),
    })
  ),
});

export const FilterBody = ({
  selected,
  onSave,
  onReset,
  customFilters,
  filterConfig,
  accordionTitle,
  showAccordion,
  showSearchInput,
}) => {
  const filtersToShow = Object.keys(filterConfig);
  const lastFilterRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const initialValues = useMemo((): any => {
    if (!selected || selected.length === 0) {
      return [
        {
          filter_by: {},
          filter_condition: FILTER_CONDITIONS[0],
          filter_by_values: [],
        },
      ];
    }

    // Normalize filters when they come from user_preference
    const normalized: any[] = selected?.map((filter: any) => {
      // If filter_by doesn't have an id, try to find matching custom filter
      if (filter?.filter_by?.value && !filter?.filter_by?.id) {
        const matchingCustom = customFilters?.find(
          (cf) =>
            cf.label === filter?.filter_by?.value ||
            cf.id === filter?.filter_by?.value
        );

        if (matchingCustom) {
          // Convert boolean values from API format (Y/N) to form format (true/false)
          let normalizedValues: any = filter?.filter_by_values;
          if (matchingCustom.kind === 'enum_boolean') {
            normalizedValues = filter?.filter_by_values?.map((val: any) =>
              val === 'Y' ? 'true' : val === 'N' ? 'false' : val
            );
          }

          return {
            ...filter,
            filter_by: {
              ...filter.filter_by,
              id: matchingCustom.id,
              label: matchingCustom.label,
              value: matchingCustom.label,
              isCustom: true,
            },
            filter_by_values: normalizedValues,
          };
        }
      } else if (filter?.filter_by?.id) {
        // Handle filters that already have an ID (from restored state)
        const matchingCustom = customFilters?.find(
          (cf) => cf.id === filter?.filter_by?.id
        );

        if (matchingCustom) {
          // Convert boolean values from API format (Y/N) to form format (true/false)
          let normalizedValues: any = filter?.filter_by_values;
          if (matchingCustom.kind === 'enum_boolean') {
            normalizedValues = filter?.filter_by_values?.map((val: any) =>
              val === 'Y' ? 'true' : val === 'N' ? 'false' : val
            );
          }

          return {
            ...filter,
            filter_by_values: normalizedValues,
          };
        }
      }
      return filter;
    });
    return normalized;
  }, [selected, customFilters]);

  const formik = useFormik({
    initialValues: { filters: initialValues },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values.filters ?? []);
    },
  });

  const { values: formikValues } = formik || {};
  const { filters: formikFilters = [] } = formikValues || {};

  const nextFilters = useMemo(() => {
    const selectedFilters = formikFilters
      .map((filter) => filter.filter_by?.value)
      .filter(Boolean);

    const availableCustomFilters =
      customFilters
        ?.filter(
          (filter) =>
            FILTER_KINDS.includes(filter.kind) &&
            !filter.multi &&
            !filter.deleted &&
            !selectedFilters.includes(filter.label)
        )
        .map((filter) => filter.label) || [];

    const availableConfigFilters = filtersToShow.filter(
      (filter) => !selectedFilters.includes(filter)
    );

    return [...availableConfigFilters, ...availableCustomFilters];
  }, [filtersToShow, formikFilters, customFilters]);

  const canAddFilter = useMemo(() => {
    return nextFilters.length > 0;
  }, [nextFilters]);

  const [expandedFilters, setExpandedFilters] = useState(false);

  const allowApplyFilters = useMemo(() => {
    return (
      formikFilters.length > 0 &&
      formikFilters.every((filter) => {
        const values = filter?.filter_by_values;
        return (
          values !== null &&
          values !== '' &&
          (Array.isArray(values) ? values?.length > 0 : true)
        );
      })
    );
  }, [formikFilters]);

  const renderHeader = () => {
    // 23px above and below a single line of text is a lot of a phone screen —
    // below md the heading only needs enough to clear the close button.
    return (
      <Box py={{ base: 'md', md: '23px' }}>
        <Flex alignItems="center" justifyContent="space-between">
          <KText variant="heading_3">In this view filter</KText>
        </Flex>
      </Box>
    );
  };

  const renderAddFilter = ({ arrayHelpers }: { arrayHelpers: any }): any => {
    if (canAddFilter && nextFilters?.length > 0) {
      return (
        <Flex
          alignItems="center"
          gap="4px"
          minW="fit-content"
          onClick={() => {
            const firstOption = nextFilters[0];
            const firstOptionConfig = filterConfig[firstOption];

            if (!firstOption) return;

            let filter_condition: any;
            if (firstOptionConfig?.isCompare) {
              filter_condition = FILTER_COMPARISON_CONDITIONS[0];
            } else if (
              // for now only custom filter date is using compare conditions
              ['date', 'number'].includes(
                customFilters?.find((filter) => filter?.label === firstOption)
                  ?.kind
              )
            ) {
              filter_condition = FILTER_COMPARISON_CONDITIONS[0];
            } else {
              filter_condition = FILTER_CONDITIONS[0];
            }

            // Check if this is a custom filter
            const customFilter = customFilters?.find(
              (f) => f.label === firstOption
            );

            const initialFilter: any = {
              filter_by: {
                value: firstOption,
                label: firstOptionConfig?.filterLabel || firstOption,
                ...(customFilter && {
                  id: customFilter.id,
                  isCustom: true,
                }),
              },
              filter_condition,
              filter_by_values: [],
            };

            arrayHelpers.push(initialFilter);

            // Use container-specific scroll
            setTimeout(() => {
              if (scrollContainerRef.current && lastFilterRef.current) {
                const container = scrollContainerRef.current;
                const newElement = lastFilterRef.current;

                container.scrollTo({
                  top: newElement.offsetTop - container.offsetTop,
                  behavior: 'smooth',
                });
              }
            }, 0);
          }}
          cursor="pointer"
        >
          <Circle bg="secondary-50" p="2px">
            <Icon as={Plus} width="14px" height="14px" color="primary-500" />
          </Circle>
          <KText variant="heading_3" color="primary-500">
            Add Filter
          </KText>
        </Flex>
      );
    }
    return null;
  };

  const renderFilterCta = () => {
    return (
      <Flex gap="12px" justifyContent="flex-end" w="100%">
        <Button
          variant="secondary"
          onClick={() => {
            formik.resetForm();
            onReset();
          }}
        >
          Clear all
        </Button>
        <Button variant="primary" type="submit" isDisabled={!allowApplyFilters}>
          Apply
        </Button>
      </Flex>
    );
  };

  const renderFooter = ({ arrayHelpers }: { arrayHelpers: any }): any => {
    return (
      <Flex
        borderTop="1px solid"
        borderColor="border-color"
        mt={{ base: 'sm', md: '11px' }}
        py={{ base: 'sm', md: '14px' }}
        alignItems="center"
        justifyContent="space-between"
        w="100%"
      >
        {renderAddFilter({ arrayHelpers })}
        {renderFilterCta()}
      </Flex>
    );
  };

  const handleFormKeyDown = (e) => {
    // Prevent form submission on Enter key in input fields -
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    // Popover gutter: 23px each side costs ~12% of a 375px screen, and the
    // filter rows inside are already tight.
    <Box px={{ base: 'lg', md: '23px' }}>
      {renderHeader()}
      <FormikProvider value={formik}>
        <Form noValidate onKeyDown={handleFormKeyDown}>
          <FieldArray
            name="filters"
            render={(arrayHelpers: ArrayHelpers) => {
              return (
                <>
                  <Box>
                    <Box
                      ref={scrollContainerRef}
                      overflowY="auto"
                      maxHeight="calc(100vh - 400px)"
                    >
                      {formik.values?.filters?.map((filter, idx) => (
                        <div
                          key={`filter-${idx}-${filter.filter_by?.value}`}
                          ref={
                            idx === formik.values.filters.length - 1
                              ? lastFilterRef
                              : null
                          }
                        >
                          <FilterRow
                            idx={idx}
                            arrayHelpers={arrayHelpers}
                            currentSelectedFilter={filter}
                            options={nextFilters}
                            customFilters={customFilters}
                            expanded={expandedFilters}
                            previousSelectedFilter={formikFilters}
                            appliedFilters={selected}
                            toggleExpandCollapse={() =>
                              setExpandedFilters((prev) => !prev)
                            }
                            filterConfig={filterConfig}
                            accordionTitle={accordionTitle}
                            showAccordion={showAccordion}
                            showSearchInput={showSearchInput}
                          />
                        </div>
                      ))}
                    </Box>
                  </Box>

                  {renderFooter({ arrayHelpers })}
                </>
              );
            }}
          />
        </Form>
      </FormikProvider>
    </Box>
  );
};
